/**
 * ORG-7 — transfert d'un projet de l'espace personnel vers une équipe de l'organisation.
 * Sens unique ; la clé n'est jamais renommée d'office : sur `PROJECT_KEY_TAKEN`, on propose la
 * clé suggérée par le serveur et l'utilisateur la confirme ou la change.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { roleAtLeast } from '@exiplan/shared-types';
import { api } from '../lib/api.js';
import { fetchAllPages } from '../lib/pagination.js';
import { useAsync } from '../hooks/useAsync.js';
import { appAlert, appConfirm, appPrompt } from '../dialogs/service.js';
import { Eyebrow, Panel } from '../components/common.jsx';
import { BTN_GHOST_SM, FORM_FIELD, FORM_LABEL, FORM_ROW, SELECT_SM } from '../ui/theme.js';

export function MoveToOrganizationSettings({ projectId, projectKey, onTransferred }) {
  const { t } = useTranslation();
  // M7 : toutes les équipes, page après page — au-delà de 50 (voire 200), l'équipe cible
  // pouvait manquer à la liste.
  const { data } = useAsync((signal) => fetchAllPages('/teams', { signal }), []);
  // Seule une adhésion `active` ouvre le transfert côté serveur (404 sinon) : une équipe dont
  // l'organisation est `suspended`/`requested` n'est donc pas proposée.
  const { data: organizationsData } = useAsync(
    (signal) => api.get('/me/organizations', { signal }),
    [],
  );
  const actives = new Set(
    (organizationsData?.data ?? [])
      .filter((organization) => organization.status === 'active')
      .map((organization) => organization.id),
  );
  const eligibles = (data?.data ?? []).filter(
    (team) =>
      team.organizationKind === 'organisation' &&
      actives.has(team.organizationId) &&
      roleAtLeast(team.role, 'admin'),
  );
  const [teamId, setTeamId] = useState('');
  const cible = teamId || eligibles[0]?.id || '';

  async function transferer(key) {
    try {
      await api.post(
        `/projects/${projectId}/transfer`,
        key ? { teamId: cible, key } : { teamId: cible },
      );
      onTransferred();
    } catch (error) {
      if (error.details?.code === 'PROJECT_KEY_TAKEN') {
        const nouvelle = await appPrompt(
          t('settings.transfer.keyTakenPrompt', { key: key ?? projectKey }),
          error.details.suggestedKey ?? '',
        );
        if (nouvelle && nouvelle.trim()) await transferer(nouvelle.trim().toUpperCase());
        return;
      }
      await appAlert(t('settings.transfer.failed', { message: error.message }));
    }
  }

  async function onClick() {
    if (!(await appConfirm(t('settings.transfer.confirm')))) return;
    await transferer();
  }

  return (
    <Panel>
      <Eyebrow>{t('settings.transfer.title')}</Eyebrow>
      <p className="mt-2 text-sm text-ink-soft">{t('settings.transfer.description')}</p>
      {eligibles.length === 0 ? (
        <p className="mt-3 text-sm text-ink-soft">{t('settings.transfer.noTeam')}</p>
      ) : (
        <div className={`mt-3 ${FORM_ROW}`}>
          <div className={FORM_FIELD}>
            <label className={FORM_LABEL} htmlFor="transfer-team">
              {t('settings.transfer.teamLabel')}
            </label>
            <select
              id="transfer-team"
              className={SELECT_SM}
              value={cible}
              onChange={(e) => setTeamId(e.target.value)}
            >
              {eligibles.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.organizationName} — {team.name}
                </option>
              ))}
            </select>
          </div>
          <button type="button" className={BTN_GHOST_SM} onClick={onClick}>
            {t('settings.transfer.action')}
          </button>
        </div>
      )}
    </Panel>
  );
}
